import { useRef, useEffect, useCallback } from 'react';
import { useSurveyAnswer } from 'hooks/surveys/queries';
import { useTranscript } from 'hooks/transcripts/queries';

/**
 * Hook to provide a stable reference to codeable text content and a substring function
 * Works with both SurveyAnswer and Transcript types
 * @param {string} codeableType - The type of codeable item ('SurveyAnswer' or 'Transcript')
 * @param {string|number} codeableId - The ID of the codeable item
 * @returns {Object} - Contains dataRef, getSubstring function, and isLoading state
 */
export const useCodeableTextRef = (codeableType, codeableId) => {
  // Get survey answer if needed
  const { data: surveyAnswer, isLoading: surveyLoading } = useSurveyAnswer(
    codeableType === 'SurveyAnswer' ? codeableId : null
  );
  
  // Get transcript if needed
  const { data: transcriptData, isLoading: transcriptLoading } = useTranscript(
    codeableType === 'Transcript' ? codeableId : null
  );

  const data = codeableType === 'SurveyAnswer' ? surveyAnswer
    : codeableType === 'Transcript' ? transcriptData
    : null;

  // Store the data in refs to avoid re-renders
  const dataRef = useRef(null);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  
  /**
   * Get a substring from the current data
   * @param {number} start - Start index
   * @param {number} end - End index
   * @returns {string} - The substring or empty string if no data
   */
  const getSubstring = useCallback((start, end) => {
    if (!dataRef.current) return "";
    
    const body = dataRef.current?.body;
    return body ? body.substring(start, end) : "";
  }, []);
  
  return {
    dataRef,
    getSubstring,
    // Reactive body text, for callers that need to respond to the codeable
    // text changing (unlike getSubstring, which reads a ref and can't).
    body: data?.body,
    isLoading: surveyLoading || transcriptLoading
  };
};
